/**
 * Shared search parameter schema for all ramps routes
 * Ensures consistent URL state preservation across the entire flow
 */

import type { ServiceOptionCode } from "@/api/types/moneygram";
import { normalizeOperation } from "@/lib/normalize-operation";
import { rampsStore } from "@/stores/ramps-store";
import { extractClientNameFromJwt } from "@/utils/jwt-decoder";

export interface RampsSearchSchema {
	// External partner params (from partner URL)
	// For v3 (JWT): token is passed, but operation/lang/asset_code/wallet_provider come from prepare endpoint
	// For v5 (OAuth): all params come from URL
	token?: string; // JWT token for v3 API (contains wallet address)
	accesstoken?: string; // OAuth token for v5 API
	transaction_id: string;
	wallet_provider: string; // v5 only - for v3, comes from prepare endpoint
	asset_code: string; // v5 only - for v3, comes from prepare endpoint
	operation: string; // v5 only - for v3, comes from prepare endpoint
	lang: string; // v5 only - for v3, comes from prepare endpoint
	walletAddress?: string; // Wallet address for V5 API (required when using accesstoken)
	refNumber?: string; // Reference number - present in v2 standalone status pages

	// Internal flow params (also from URL for bookmarking/sharing)
	amount: string;
	country: string; // DEPRECATED: Legacy field for backward compatibility
	payout_country?: string; // Where user wants to receive money
	country_src?: "manual" | "url" | "profile" | "service"; // Source of payout_country
	locks?: string; // Comma-separated list of locked fields
	state: string;
	step: string;
	from_review?: string; // Flag indicating navigation from review page
	issueCountry?: string; // ID issue country selection
	service_option?: ServiceOptionCode;
}

export function validateRampsSearch(
	search: Record<string, unknown>,
): RampsSearchSchema {
	return {
		// External partner params (from partner URL)
		token: search.token as string | undefined,
		accesstoken: search.accesstoken as string | undefined,
		transaction_id: (search.transaction_id as string) || "",
		wallet_provider: (search.wallet_provider as string) || "",
		asset_code: (search.asset_code as string) || "USDC",
		operation: normalizeOperation((search.operation as string) || "withdraw"),
		// When the partner didn't include `lang=` in the URL, fall back to the
		// active store locale tag (seeded at bootstrap from `app.languages` /
		// `lang` / browser detection). This keeps the URL consistent with what
		// the SDK actually activated — partners testing with `app.languages=
		// pt-PT,en` no longer see a stale `lang=en` injected by the router.
		lang: (search.lang as string) || rampsStore.state.lang || "en",
		walletAddress: search.walletAddress as string | undefined,
		refNumber: search.refNumber as string | undefined,

		// Internal flow params (also from URL for bookmarking/sharing)
		amount: (search.amount as string) || "",
		country: (search.country as string) || "", // DEPRECATED: Keep for backward compatibility, no default
		payout_country: search.payout_country as string | undefined,
		country_src: search.country_src as
			| "manual"
			| "url"
			| "profile"
			| "service"
			| undefined,
		locks: search.locks as string | undefined,
		state: (search.state as string) || "",
		step: (search.step as string) || "calculator",
		from_review: search.from_review as string | undefined,
		issueCountry: search.issueCountry as string | undefined,
		service_option: search.service_option as ServiceOptionCode | undefined,
	};
}

/**
 * Helper: Checks if search params contain v2 context (operation, wallet_provider, asset_code)
 */
function hasV2ContextParams(search: RampsSearchSchema): boolean {
	return !!(search.operation && search.wallet_provider && search.asset_code);
}

/**
 * Helper: Checks if JWT token has client_name claim (definitive v3 indicator)
 */
function hasV3ClientName(token: string): boolean {
	return !!extractClientNameFromJwt(token);
}

/**
 * Helper: Determines if current page is a standalone transaction status page
 * A standalone transaction status page is one that:
 * - Is on /transaction-status path
 * - Has a JWT token (not OAuth token)
 * - Does NOT have v2 context params (operation, wallet_provider, asset_code)
 * - Does NOT have refNumber
 *
 * @param search - Search parameters from URL
 * @param pathname - Optional pathname (defaults to window.location.pathname)
 */
export function isStandaloneTransactionStatusPage(
	search: RampsSearchSchema,
	pathname?: string,
): boolean {
	const currentPathname =
		pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
	return (
		currentPathname.includes("/transaction-status") &&
		!!search.token &&
		!search.accesstoken &&
		!hasV2ContextParams(search) &&
		!search.refNumber
	);
}

/**
 * Determines API version based on which token parameter is present in the URL
 * and whether context params are provided.
 *
 * IMPORTANT: This function uses explicit detection only - no ambiguous defaults.
 * Returns null if version cannot be determined from URL params alone.
 *
 * Decision tree (in priority order):
 * 1. accesstoken → v5 API (OAuth flow, all params from URL)
 * 2. no token → null (cannot determine, requires store or default)
 * 3. token with client_name in JWT → v3 API (definitive v3 indicator; evaluated before URL context
 *    so v3 flows that hydrate operation/wallet_provider/asset_code after /prepare stay v3)
 * 4. token + v2 context params (operation, wallet_provider, asset_code) → v2 API
 * 5. token + refNumber → v2 API (standalone status page pattern)
 * 6. token on standalone transaction status page WITHOUT client_name → v2 API (legacy fallback)
 * 7. token without v2 indicators → v3 API (default JWT flow, uses prepare endpoint)
 *
 * @param search - Search parameters from URL
 * @param isStandaloneTransactionStatus - Optional flag indicating standalone transaction status page
 * @returns API version if determinable from URL, null otherwise
 */
export function getApiVersionFromSearchParams(
	search: RampsSearchSchema,
	isStandaloneTransactionStatus?: boolean,
): "v2" | "v3" | "v5" | null {
	// Priority 1: OAuth token → v5 (always explicit)
	if (search.accesstoken) {
		return "v5";
	}

	// No token → cannot determine from URL alone
	if (!search.token) {
		return null;
	}

	// Priority 2: client_name in JWT → definitive v3 (before URL context triple; v3 /prepare
	// hydrates the same shape into the URL as legacy v2 partner links)
	if (hasV3ClientName(search.token)) {
		return "v3";
	}

	// Priority 3: v2 indicators (context params or refNumber) → v2 (explicit)
	if (hasV2ContextParams(search) || search.refNumber) {
		return "v2";
	}

	// Priority 4: Standalone transaction status without client_name → v2 fallback (explicit)
	// Only applies to legacy v2 transactions without client_name
	if (isStandaloneTransactionStatus) {
		return "v2";
	}

	// Priority 5: JWT token without v2 indicators → v3 (default JWT flow, uses prepare endpoint)
	// This is the expected behavior: JWT without URL context defaults to v3
	return "v3";
}

/**
 * Gets the auth token from search params (either token or accesstoken)
 */
export function getAuthTokenFromSearchParams(
	search: RampsSearchSchema,
): string {
	return search.accesstoken || search.token || "";
}
